import { Document, Font, Image, Link, Page, StyleSheet, Text, View } from '@react-pdf/renderer';

import { getDisplayPhotoUrl, hexToRgba } from './cvDefaults';
import { buildContactLinks, filterEmptyEntries, FONT_SIZE_MAP, parseDescription, resolveUrlHref, SIDEBAR_SECTION_TYPES, type ContactLink, type FontSizes } from './pdfUtils';
import { CVData, CVEntry, CVSection, CVSettings, CVTemplate, GDPR_CONSENT_TEXT } from '../types';

// Register fonts — @react-pdf/renderer requires TTF format (woff2 is NOT supported)
Font.register({
  family: 'Inter',
  fonts: [
    { src: 'https://fonts.gstatic.com/s/inter/v20/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuLyfMZg.ttf' },
    { src: 'https://fonts.gstatic.com/s/inter/v20/UcCM3FwrK3iLTcvneQg7Ca725JhhKnNqk4j1ebLhAm8SrXTc2dtRipWA.ttf', fontStyle: 'italic' },
    { src: 'https://fonts.gstatic.com/s/inter/v20/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuGKYMZg.ttf', fontWeight: 600 },
    { src: 'https://fonts.gstatic.com/s/inter/v20/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuFuYMZg.ttf', fontWeight: 700 },
  ],
});

Font.register({
  family: 'Poppins',
  fonts: [
    { src: 'https://fonts.gstatic.com/s/poppins/v24/pxiEyp8kv8JHgFVrFJA.ttf' },
    { src: 'https://fonts.gstatic.com/s/poppins/v24/pxiGyp8kv8JHgFVrJJLucHtF.ttf', fontStyle: 'italic' },
    { src: 'https://fonts.gstatic.com/s/poppins/v24/pxiByp8kv8JHgFVrLEj6V1s.ttf', fontWeight: 600 },
    { src: 'https://fonts.gstatic.com/s/poppins/v24/pxiByp8kv8JHgFVrLCz7V1s.ttf', fontWeight: 700 },
  ],
});

Font.register({
  family: 'Lato',
  fonts: [
    { src: 'https://fonts.gstatic.com/s/lato/v25/S6uyw4BMUTPHvxk.ttf' },
    { src: 'https://fonts.gstatic.com/s/lato/v25/S6u8w4BMUTPHjxsAXC-v.ttf', fontStyle: 'italic' },
    { src: 'https://fonts.gstatic.com/s/lato/v25/S6u9w4BMUTPHh6UVew8.ttf', fontWeight: 600 },
    { src: 'https://fonts.gstatic.com/s/lato/v25/S6u9w4BMUTPHh6UVew8.ttf', fontWeight: 700 },
  ],
});

Font.register({
  family: 'Roboto',
  fonts: [
    { src: 'https://fonts.gstatic.com/s/roboto/v50/KFOMCnqEu92Fr1ME7kSn66aGLdTylUAMQXC89YmC2DPNWubEbWmT.ttf' },
    { src: 'https://fonts.gstatic.com/s/roboto/v50/KFOKCnqEu92Fr1Mu53ZEC9_Vu3r1gIhOszmOClHrs6ljXfMMLoHQuAj-lg.ttf', fontStyle: 'italic' },
    { src: 'https://fonts.gstatic.com/s/roboto/v50/KFOMCnqEu92Fr1ME7kSn66aGLdTylUAMQXC89YmC2DPNWuYjammT.ttf', fontWeight: 600 },
    { src: 'https://fonts.gstatic.com/s/roboto/v50/KFOMCnqEu92Fr1ME7kSn66aGLdTylUAMQXC89YmC2DPNWuYjammT.ttf', fontWeight: 700 },
  ],
});

Font.register({
  family: 'Merriweather',
  fonts: [
    { src: 'https://fonts.gstatic.com/s/merriweather/v33/u-4D0qyriQwlOrhSvowK_l5UcA6zuSYEqOzpPe3HOZJ5eX1WtLaQwmYiScCmDxhtNOKl8yDr3icqEw.ttf' },
    { src: 'https://fonts.gstatic.com/s/merriweather/v33/u-4B0qyriQwlOrhSvowK_l5-eTxCVx0ZbwLvKH2Gk9hLmp0v5yA-xXPqCzLvPee1XYk_XSf-FmTCUG33AvQ.ttf', fontStyle: 'italic' },
    { src: 'https://fonts.gstatic.com/s/merriweather/v33/u-4D0qyriQwlOrhSvowK_l5UcA6zuSYEqOzpPe3HOZJ5eX1WtLaQwmYiScCmDxhtNOKl8yDrOSAqEw.ttf', fontWeight: 600 },
    { src: 'https://fonts.gstatic.com/s/merriweather/v33/u-4D0qyriQwlOrhSvowK_l5UcA6zuSYEqOzpPe3HOZJ5eX1WtLaQwmYiScCmDxhtNOKl8yDrOSAqEw.ttf', fontWeight: 700 },
  ],
});

// Disable word hyphenation — prevents ugly breaks mid-word
Font.registerHyphenationCallback((word) => [word]);

// ─── Shared entry renderer ──────────────────────────────────────

interface EntryRendererProps {
  entry: CVEntry;
  sizes: FontSizes;
  accentColor: string;
  titleColor: string;
  subtitleColor: string;
  dateColor: string;
  bodyColor: string;
  tagBg: string;
  tagColor: string;
  showLocation?: boolean;
}

const RenderEntry = ({
  entry, sizes, accentColor, titleColor, subtitleColor, dateColor, bodyColor, tagBg, tagColor, showLocation = true,
}: EntryRendererProps) => {
  const segments = parseDescription(entry.description || '');

  return (
    <View style={{ marginBottom: 12 }}>
      <View wrap={false} style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <View style={{ flex: 1, paddingRight: 8 }}>
          <Text style={{ fontSize: sizes.title, fontWeight: 'bold', color: titleColor }}>{entry.title}</Text>
          {!!entry.subtitle && (
            <Text style={{ fontSize: sizes.body, color: subtitleColor, marginTop: 1 }}>
              {entry.subtitle}
              {showLocation && entry.location ? <Text style={{ color: dateColor }}> — {entry.location}</Text> : null}
            </Text>
          )}
        </View>
        {!!(entry.startDate || entry.endDate) && (
          <Text style={{ fontSize: sizes.body - 0.5, color: dateColor, fontWeight: 600, textAlign: 'right', width: 120, flexShrink: 0 }}>
            {entry.startDate}
            {entry.endDate ? ` – ${entry.endDate}` : ''}
          </Text>
        )}
      </View>

      {segments.length > 0 && (
        <View style={{ marginTop: 3 }}>
          {segments.map((seg, i) => {
            if (seg.type === 'break') return <View key={i} style={{ height: 4 }} />;
            if (seg.type === 'bullet') return (
              <View key={i} style={{ flexDirection: 'row', marginTop: 2, paddingLeft: 2 }}>
                <Text style={{ fontSize: sizes.body, color: accentColor, marginRight: 5, lineHeight: 1.6 }}>•</Text>
                <Text style={{ fontSize: sizes.body, color: bodyColor, lineHeight: 1.6, flex: 1 }}>{seg.text}</Text>
              </View>
            );
            return (
              <Text key={i} style={{ fontSize: sizes.body, color: bodyColor, lineHeight: 1.6 }}>{seg.text}</Text>
            );
          })}
        </View>
      )}

      {entry.tags.length > 0 && (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginTop: 3 }}>
          {entry.tags.map((tag) => (
            <Text key={tag} style={{ fontSize: sizes.body - 1.5, backgroundColor: tagBg, paddingHorizontal: 5, paddingVertical: 2, borderRadius: 2, marginRight: 3, marginBottom: 2, color: tagColor }}>
              {tag}
            </Text>
          ))}
        </View>
      )}

      {!!(entry.url || entry.previewUrl) && (
        <View style={{ flexDirection: 'row', marginTop: 3 }}>
          {!!entry.url && (
            <Link src={resolveUrlHref(entry.url) || entry.url} style={{ fontSize: sizes.body - 0.5, color: accentColor, marginRight: 10, textDecoration: 'none' }}>
              {entry.urlLabel || 'GitHub'}
            </Link>
          )}
          {!!entry.previewUrl && (
            <Link src={resolveUrlHref(entry.previewUrl) || entry.previewUrl} style={{ fontSize: sizes.body - 0.5, color: accentColor, marginRight: 10, textDecoration: 'none' }}>
              {entry.previewUrlLabel || 'Live Demo'}
            </Link>
          )}
        </View>
      )}
    </View>
  );
};

// ─── Shared certification renderer ──────────────────────────────

const RenderCertification = ({
  entry, sizes, titleColor, subtitleColor, dateColor, accentColor,
}: {
  entry: CVEntry;
  sizes: FontSizes;
  titleColor: string;
  subtitleColor: string;
  dateColor: string;
  accentColor: string;
}) => (
  <View style={{ marginBottom: 6 }} wrap={false} minPresenceAhead={30}>
    <Text style={{ fontSize: sizes.title, fontWeight: 'bold', color: titleColor }}>{entry.title}</Text>
    {!!entry.subtitle && (
      <Text style={{ fontSize: sizes.body, color: subtitleColor, fontStyle: 'italic' }}>{entry.subtitle}</Text>
    )}
    {!!(entry.startDate || entry.endDate) && (
      <Text style={{ fontSize: sizes.body, color: dateColor }}>
        {entry.startDate}
        {entry.endDate ? ` – ${entry.endDate}` : ''}
      </Text>
    )}
    {!!entry.location && (
      <Text style={{ fontSize: sizes.body, color: dateColor, marginTop: 1 }}>
        ID: {entry.location}
      </Text>
    )}
    {!!entry.url && (
      <Link src={resolveUrlHref(entry.url) || entry.url} style={{ fontSize: sizes.body, color: accentColor, marginTop: 1, textDecoration: 'none' }}>
        Verify Credential
      </Link>
    )}
  </View>
);

// ─── Shared skill/hobby tags renderer ───────────────────────────

const RenderTagSection = ({
  section, sizes, tagBg, tagColor, labelColor, verticalCategories,
}: {
  section: CVSection;
  sizes: FontSizes;
  tagBg: string;
  tagColor: string;
  labelColor: string;
  verticalCategories?: boolean;
}) => {
  if (section.type === 'skills') {
    const categorized: Record<string, string[]> = {};
    section.entries.forEach((entry) => {
      if (!entry.title) return;
      const category = entry.subtitle || 'General';
      if (!categorized[category]) categorized[category] = [];
      categorized[category].push(entry.title);
    });
    const categories = Object.entries(categorized);
    const hasMultipleCategories = categories.length > 1;

    return (
      <View>
        {categories.map(([category, skills]) => (
          <View key={category} wrap={false} style={hasMultipleCategories && !verticalCategories
            ? { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 3 }
            : { marginBottom: 4 }
          }>
            {hasMultipleCategories && category !== 'General' && (
              <Text style={{ fontSize: sizes.body, fontWeight: 600, color: labelColor, minWidth: 70, marginRight: 6, marginBottom: verticalCategories ? 3 : 0 }}>
                {category}
              </Text>
            )}
            <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
              {skills.map((skill, i) => (
                <Text key={i} style={{ fontSize: sizes.body, backgroundColor: tagBg, paddingHorizontal: 6, paddingVertical: 3, borderRadius: 2, marginRight: 4, marginBottom: 3, color: tagColor }}>
                  {skill}
                </Text>
              ))}
            </View>
          </View>
        ))}
      </View>
    );
  }

  // hobbies, interests — simple tag chips
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
      {section.entries.filter((entry) => entry.title).map((entry) => (
        <Text key={entry.id} style={{ fontSize: sizes.body, backgroundColor: tagBg, paddingHorizontal: 6, paddingVertical: 3, borderRadius: 10, marginRight: 4, marginBottom: 3, color: tagColor }}>
          {entry.title}
        </Text>
      ))}
    </View>
  );
};

// ─── Shared languages renderer ──────────────────────────────────

const RenderLanguages = ({
  section, sizes, labelColor, levelColor, vertical,
}: {
  section: CVSection;
  sizes: FontSizes;
  labelColor: string;
  levelColor: string;
  vertical?: boolean;
}) => (
  <View style={vertical ? {} : { flexDirection: 'row', flexWrap: 'wrap' }}>
    {filterEmptyEntries(section.entries).map((entry) => (
      <View key={entry.id} style={vertical
        ? { flexDirection: 'row', marginBottom: 2 }
        : { flexDirection: 'row', marginRight: 14 }
      }>
        <Text style={{ fontSize: sizes.body, fontWeight: 600, color: labelColor }}>{entry.title}</Text>
        {!!entry.subtitle && (
          <Text style={{ fontSize: sizes.body, color: levelColor, marginLeft: 4 }}>({entry.subtitle})</Text>
        )}
      </View>
    ))}
  </View>
);

// ─── GDPR renderer ──────────────────────────────────────────────

const RenderGDPR = ({ sizes, accentColor }: { sizes: FontSizes; accentColor: string }) => (
  <View style={{ marginTop: 12, paddingTop: 6 }}>
    <View style={{ borderBottomWidth: 0.5, borderBottomColor: accentColor, opacity: 0.2, marginBottom: 6 }} />
    <Text style={{ fontSize: sizes.gdpr, color: '#a0aec0', lineHeight: 1.5, fontStyle: 'italic' }}>
      {GDPR_CONSENT_TEXT}
    </Text>
  </View>
);

// ─── Shared contact link renderer ────────────────────────────────

const RenderContactItem = ({
  item, style,
}: {
  item: ContactLink;
  style: { fontSize: number; color: string; marginBottom?: number };
}) => {
  if (item.href) {
    return (
      <Link src={item.href} style={{ ...style, textDecoration: 'none' }}>
        {item.display}
      </Link>
    );
  }
  return <Text style={style}>{item.display}</Text>;
};

// ═══════════════════════════════════════════════════════════════════
// CLASSIC TEMPLATE
// ═══════════════════════════════════════════════════════════════════

const ClassicLayout = ({ data, settings }: { data: CVData; settings: CVSettings }) => {
  const sizes = FONT_SIZE_MAP[settings.fontSize];
  const hasBg = settings.headerBackground;
  const accentBg = hexToRgba(settings.accentColor, settings.accentOpacity ?? 1);
  const { personalInfo, sections } = data;

  const contactLinks = buildContactLinks(personalInfo);

  const styles = StyleSheet.create({
    page: {
      fontFamily: settings.fontFamily || 'Inter',
      fontSize: sizes.body,
      color: '#1a202c',
      paddingTop: 20,
    },
    header: {
      backgroundColor: hasBg ? accentBg : '#f7fafc',
      paddingHorizontal: 40,
      paddingTop: 20,
      paddingBottom: 22,
      marginTop: -20,
      borderBottomWidth: hasBg ? 0 : 1,
      borderBottomColor: hasBg ? 'transparent' : hexToRgba(settings.accentColor, 0.2),
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
  });

  const renderSection = (section: CVSection) => {
    const isTagType = section.type === 'skills' || section.type === 'hobbies' || section.type === 'interests';
    const isLangs = section.type === 'languages';
    const isCerts = section.type === 'certifications';

    return (
      <View key={section.id} style={{ marginBottom: 10 }}>
        <View wrap={false} minPresenceAhead={25}>
          <Text style={{ fontSize: sizes.section, fontWeight: 'bold', color: '#1a202c', letterSpacing: 1.5, marginBottom: 2 }}>
            {section.title}
          </Text>
          <View style={{ borderBottomWidth: 1, borderBottomColor: settings.accentColor, opacity: 0.2, marginBottom: 6, marginTop: 2 }} />
        </View>

        {isTagType ? (
          <RenderTagSection section={section} sizes={sizes} tagBg="#edf2f7" tagColor="#4a5568" labelColor="#2d3748" />
        ) : isLangs ? (
          <RenderLanguages section={section} sizes={sizes} labelColor="#2d3748" levelColor="#a0aec0" />
        ) : isCerts ? (
          filterEmptyEntries(section.entries).map((entry) => (
            <RenderCertification key={entry.id} entry={entry} sizes={sizes} titleColor="#1a202c" subtitleColor="#4a5568" dateColor="#a0aec0" accentColor={settings.accentColor} />
          ))
        ) : (
          filterEmptyEntries(section.entries).map((entry) => (
            <RenderEntry key={entry.id} entry={entry} sizes={sizes} accentColor={settings.accentColor} titleColor="#1a202c" subtitleColor="#4a5568" dateColor="#a0aec0" bodyColor="#4a5568" tagBg="#edf2f7" tagColor="#4a5568" />
          ))
        )}
      </View>
    );
  };

  const displayPhotoUrl = getDisplayPhotoUrl(personalInfo.photoUrl, personalInfo.fullName);
  const hasPhoto = settings.showPhoto && !!displayPhotoUrl;

  return (
    <Page size="A4" style={styles.page}>
      {/* Header: photo + name/title left, contact right */}
      <View style={styles.header}>
        <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
          {hasPhoto && (
            <Image src={displayPhotoUrl} style={{ width: 52, height: 52, borderRadius: 26, objectFit: 'cover', marginRight: 14, borderWidth: 2, borderColor: hasBg ? 'rgba(255,255,255,0.5)' : settings.accentColor }} />
          )}
          <View>
            <Text style={{ fontSize: sizes.name, fontWeight: 'bold', color: hasBg ? '#ffffff' : '#1a202c' }}>
              {personalInfo.fullName || 'Your Name'}
            </Text>
            <Text style={{ fontSize: sizes.title, color: hasBg ? 'rgba(255,255,255,0.8)' : '#4a5568', marginTop: 3, fontWeight: 600, letterSpacing: 0.5 }}>
              {personalInfo.jobTitle || 'Job Title'}
            </Text>
          </View>
        </View>
        {contactLinks.length > 0 && (
          <View style={{ alignItems: 'flex-end', marginTop: 3 }}>
            {contactLinks.map((item, i) => (
              <View key={i} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 2 }}>
                <Text style={{ fontSize: 4, color: hasBg ? 'rgba(255,255,255,0.4)' : settings.accentColor, marginRight: 5 }}>●</Text>
                <RenderContactItem item={item} style={{ fontSize: sizes.body - 1, color: hasBg ? 'rgba(255,255,255,0.8)' : '#4a5568' }} />
                <Text style={{ fontSize: 4, color: hasBg ? 'rgba(255,255,255,0.4)' : settings.accentColor, marginLeft: 5 }}>●</Text>
              </View>
            ))}
          </View>
        )}
      </View>

      {/* Body */}
      <View style={{ paddingHorizontal: 40, paddingTop: 14, paddingBottom: 36 }}>
        {/* Ordered content: About + Sections respecting aboutPosition */}
        {(() => {
          const aboutBlock = personalInfo.summary ? (
            <View key="__about__" style={{ marginBottom: 12 }}>
              <Text style={{ fontSize: sizes.section, fontWeight: 'bold', color: '#1a202c', letterSpacing: 1.5, marginBottom: 4 }}>
                {personalInfo.aboutTitle || 'About Me'}
              </Text>
              <Text style={{ fontSize: sizes.body, color: '#4a5568', lineHeight: 1.6 }}>
                {personalInfo.summary}
              </Text>
            </View>
          ) : null;
          const pos = data.aboutPosition ?? 0;
          const items: React.ReactNode[] = [];
          sections.forEach((section, i) => {
            if (i === pos && aboutBlock) items.push(aboutBlock);
            items.push(renderSection(section));
          });
          if (pos >= sections.length && aboutBlock) items.push(aboutBlock);
          return items;
        })()}

        {personalInfo.gdprConsent && <RenderGDPR sizes={sizes} accentColor={settings.accentColor} />}
      </View>
    </Page>
  );
};

// ═══════════════════════════════════════════════════════════════════
// MODERN TEMPLATE
// ═══════════════════════════════════════════════════════════════════

const ModernLayout = ({ data, settings }: { data: CVData; settings: CVSettings }) => {
  const sizes = FONT_SIZE_MAP[settings.fontSize];
  const hasBg = settings.headerBackground;
  const accentBg = hexToRgba(settings.accentColor, settings.accentOpacity ?? 1);
  const { personalInfo, sections } = data;
  const isReversed = settings.sidebarReversed;

  const sidebarSections = sections.filter((s) => SIDEBAR_SECTION_TYPES.includes(s.type));
  const mainSections = sections.filter((s) => !SIDEBAR_SECTION_TYPES.includes(s.type));

  // Sidebar colors depend on hasBg
  const sc = (light: string, dark: string) => (hasBg ? light : dark);

  const contactLinks = buildContactLinks(personalInfo);
  const displayPhotoUrl = getDisplayPhotoUrl(personalInfo.photoUrl, personalInfo.fullName);
  const hasPhoto = settings.showPhoto && !!displayPhotoUrl;

  const sidebarWidth = '35%';
  const mainWidth = '65%';

  const renderSidebarSection = (section: CVSection) => {
    const isTagType = section.type === 'skills' || section.type === 'hobbies' || section.type === 'interests';
    const isLangs = section.type === 'languages';
    const isCerts = section.type === 'certifications';

    return (
      <View key={section.id} style={{ marginBottom: 10 }}>
        <Text style={{ fontSize: sizes.section, fontWeight: 'bold', color: sc('#ffffff', '#1a202c'), letterSpacing: 1, marginBottom: 1 }}>
          {section.title}
        </Text>
        <View style={{ borderBottomWidth: 1, borderBottomColor: sc('rgba(255,255,255,0.3)', settings.accentColor), opacity: 0.3, marginBottom: 6, marginTop: 2 }} />

        {isTagType ? (
          <RenderTagSection section={section} sizes={sizes}
            tagBg={sc('rgba(255,255,255,0.2)', '#f7fafc')}
            tagColor={sc('rgba(255,255,255,0.9)', '#4a5568')}
            labelColor={sc('rgba(255,255,255,0.9)', '#2d3748')}
            verticalCategories
          />
        ) : isLangs ? (
          <RenderLanguages section={section} sizes={sizes}
            labelColor={sc('rgba(255,255,255,0.9)', '#4a5568')}
            levelColor={sc('rgba(255,255,255,0.6)', '#a0aec0')}
            vertical
          />
        ) : isCerts ? (
          filterEmptyEntries(section.entries).map((entry) => (
            <RenderCertification key={entry.id} entry={entry} sizes={sizes}
              titleColor={sc('#ffffff', '#1a202c')}
              subtitleColor={sc('rgba(255,255,255,0.7)', '#4a5568')}
              dateColor={sc('rgba(255,255,255,0.6)', '#a0aec0')}
              accentColor={sc('rgba(255,255,255,0.8)', settings.accentColor)}
            />
          ))
        ) : null}
      </View>
    );
  };

  const renderMainSection = (section: CVSection) => (
    <View key={section.id} style={{ marginBottom: 10 }}>
      <View wrap={false} minPresenceAhead={25}>
        <Text style={{ fontSize: sizes.section, fontWeight: 'bold', color: '#1a202c', letterSpacing: 1, marginBottom: 1 }}>
          {section.title}
        </Text>
        <View style={{ borderBottomWidth: 1, borderBottomColor: settings.accentColor, opacity: 0.3, marginBottom: 6, marginTop: 2 }} />
      </View>
      {filterEmptyEntries(section.entries).map((entry) => (
        <RenderEntry key={entry.id} entry={entry} sizes={sizes} accentColor={settings.accentColor} titleColor="#1a202c" subtitleColor="#4a5568" dateColor="#a0aec0" bodyColor="#4a5568" tagBg="#f7fafc" tagColor="#4a5568" />
      ))}
    </View>
  );

  const sidebar = (
    <View style={{
      width: sidebarWidth,
      paddingHorizontal: 20,
      color: hasBg ? '#ffffff' : '#1a202c',
    }}>
      {/* Photo */}
      {hasPhoto && (
        <View style={{ marginBottom: 12 }}>
          <Image src={displayPhotoUrl} style={{ width: 60, height: 60, borderRadius: 30, objectFit: 'cover', borderWidth: 3, borderColor: hasBg ? 'rgba(255,255,255,0.6)' : settings.accentColor }} />
        </View>
      )}

      {/* Name & Title */}
      <View style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: sizes.name, fontWeight: 'bold', color: sc('#ffffff', '#1a202c'), lineHeight: 1.2 }}>
          {personalInfo.fullName || 'Your Name'}
        </Text>
        <Text style={{ fontSize: sizes.title, color: sc('rgba(255,255,255,0.8)', '#4a5568'), marginTop: 2 }}>
          {personalInfo.jobTitle || 'Job Title'}
        </Text>
      </View>

      {/* Contact */}
      <View style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: sizes.section, fontWeight: 'bold', color: sc('#ffffff', '#1a202c'), letterSpacing: 1, marginBottom: 1 }}>
          Contact
        </Text>
        <View style={{ borderBottomWidth: 1, borderBottomColor: sc('rgba(255,255,255,0.3)', settings.accentColor), opacity: 0.3, marginBottom: 6, marginTop: 2 }} />
        {contactLinks.map((item, i) => (
          <View key={i} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 2 }}>
            <Text style={{ fontSize: 4, color: sc('rgba(255,255,255,0.4)', settings.accentColor), marginRight: 5 }}>●</Text>
            <RenderContactItem item={item} style={{ fontSize: sizes.body, color: sc('rgba(255,255,255,0.9)', '#4a5568') }} />
            <Text style={{ fontSize: 4, color: sc('rgba(255,255,255,0.4)', settings.accentColor), marginLeft: 5 }}>●</Text>
          </View>
        ))}
      </View>

      {sidebarSections.map((section) => renderSidebarSection(section))}
    </View>
  );

  const main = (
    <View style={{ width: mainWidth, paddingHorizontal: 20, flex: 1 }}>
      {/* Ordered content: About + Main sections respecting aboutPosition */}
      {(() => {
        const aboutBlock = personalInfo.summary ? (
          <View key="__about__" style={{ marginBottom: 10 }}>
            <Text style={{ fontSize: sizes.section, fontWeight: 'bold', color: '#1a202c', letterSpacing: 1, marginBottom: 1 }}>
              {personalInfo.aboutTitle || 'About Me'}
            </Text>
            <View style={{ borderBottomWidth: 1, borderBottomColor: settings.accentColor, opacity: 0.3, marginBottom: 6, marginTop: 2 }} />
            <Text style={{ fontSize: sizes.body, color: '#4a5568', lineHeight: 1.6 }}>
              {personalInfo.summary}
            </Text>
          </View>
        ) : null;
        const aboutPos = data.aboutPosition ?? 0;
        let mainIdx = 0;
        let aboutMainPos = 0;
        for (let si = 0; si < sections.length; si++) {
          if (si === aboutPos) { aboutMainPos = mainIdx; break; }
          if (!SIDEBAR_SECTION_TYPES.includes(sections[si].type)) mainIdx++;
        }
        if (aboutPos >= sections.length) aboutMainPos = mainSections.length;
        const items: React.ReactNode[] = [];
        mainSections.forEach((section, i) => {
          if (i === aboutMainPos && aboutBlock) items.push(aboutBlock);
          items.push(renderMainSection(section));
        });
        if (aboutMainPos >= mainSections.length && aboutBlock) items.push(aboutBlock);
        return items;
      })()}

      {personalInfo.gdprConsent && <RenderGDPR sizes={sizes} accentColor={settings.accentColor} />}
    </View>
  );

  // A4 width = 595.28pt
  const sidebarPt = 595.28 * 0.35;

  return (
    <Page size="A4" style={{
      fontFamily: settings.fontFamily || 'Inter',
      fontSize: sizes.body,
      color: '#1a202c',
      flexDirection: isReversed ? 'row-reverse' : 'row',
      paddingTop: 20,
      paddingBottom: 20,
    }}>
      {/* Fixed sidebar background — repeats on every page */}
      <View fixed style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        [isReversed ? 'right' : 'left']: 0,
        width: sidebarPt,
        backgroundColor: hasBg ? accentBg : '#f7fafc',
        borderRightWidth: !hasBg && !isReversed ? 1 : 0,
        borderLeftWidth: !hasBg && isReversed ? 1 : 0,
        borderRightColor: !hasBg ? hexToRgba(settings.accentColor, 0.3) : 'transparent',
        borderLeftColor: !hasBg ? hexToRgba(settings.accentColor, 0.3) : 'transparent',
      }} />
      {sidebar}
      {main}
    </Page>
  );
};

// ═══════════════════════════════════════════════════════════════════
// CREATIVE TEMPLATE
// ═══════════════════════════════════════════════════════════════════

const CreativeLayout = ({ data, settings }: { data: CVData; settings: CVSettings }) => {
  const sizes = FONT_SIZE_MAP[settings.fontSize];
  const hasBg = settings.headerBackground;
  const accentBg = hexToRgba(settings.accentColor, settings.accentOpacity ?? 1);
  const { personalInfo, sections } = data;

  const contactLinks = buildContactLinks(personalInfo);
  const displayPhotoUrl = getDisplayPhotoUrl(personalInfo.photoUrl, personalInfo.fullName);
  const hasPhoto = settings.showPhoto && !!displayPhotoUrl;

  const renderSection = (section: CVSection) => {
    const isTagType = section.type === 'skills' || section.type === 'hobbies' || section.type === 'interests';
    const isLangs = section.type === 'languages';
    const isCerts = section.type === 'certifications';

    return (
      <View key={section.id} style={{ marginBottom: 14 }}>
        {/* Section header with accent bar */}
        <View wrap={false} minPresenceAhead={25} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
          <View style={{ width: 4, height: 16, backgroundColor: settings.accentColor, borderRadius: 2, marginRight: 8 }} />
          <Text style={{ fontSize: sizes.section, fontWeight: 'bold', color: '#1a202c', letterSpacing: 1 }}>
            {section.title}
          </Text>
          <View style={{ flex: 1, borderBottomWidth: 0.5, borderBottomColor: settings.accentColor, opacity: 0.3, marginLeft: 8 }} />
        </View>

        <View style={{ paddingLeft: 12 }}>
          {isTagType ? (
            <RenderTagSection section={section} sizes={sizes} tagBg={`${settings.accentColor}15`} tagColor="#4a5568" labelColor="#2d3748" />
          ) : isLangs ? (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
              {filterEmptyEntries(section.entries).map((entry) => (
                <View key={entry.id} style={{ alignItems: 'center', marginRight: 16, marginBottom: 4 }}>
                  <Text style={{ fontSize: sizes.title, fontWeight: 600, color: '#4a5568' }}>{entry.title}</Text>
                  {!!entry.subtitle && (
                    <Text style={{ fontSize: sizes.body, color: '#a0aec0' }}>{entry.subtitle}</Text>
                  )}
                </View>
              ))}
            </View>
          ) : isCerts ? (
            filterEmptyEntries(section.entries).map((entry) => (
              <RenderCertification key={entry.id} entry={entry} sizes={sizes} titleColor="#1a202c" subtitleColor={settings.accentColor} dateColor="#a0aec0" accentColor={settings.accentColor} />
            ))
          ) : (
            filterEmptyEntries(section.entries).map((entry) => (
              <RenderEntry key={entry.id} entry={entry} sizes={sizes} accentColor={settings.accentColor}
                titleColor="#1a202c" subtitleColor={settings.accentColor} dateColor="#a0aec0" bodyColor="#4a5568"
                tagBg={`${settings.accentColor}10`} tagColor={settings.accentColor}
              />
            ))
          )}
        </View>
      </View>
    );
  };

  return (
    <Page size="A4" style={{
      fontFamily: settings.fontFamily || 'Inter',
      fontSize: sizes.body,
      color: '#1a202c',
      paddingTop: 20,
    }}>
      {/* Header */}
      <View style={{
        backgroundColor: hasBg ? accentBg : '#f7fafc',
        paddingHorizontal: 32,
        paddingTop: 20,
        paddingBottom: 20,
        marginTop: -20,
        borderBottomWidth: hasBg ? 0 : 0.5,
        borderBottomColor: hasBg ? 'transparent' : hexToRgba(settings.accentColor, 0.3),
      }}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          {hasPhoto && (
            <Image src={displayPhotoUrl} style={{ width: 52, height: 52, borderRadius: 26, objectFit: 'cover', marginRight: 14, borderWidth: 2, borderColor: hasBg ? 'rgba(255,255,255,0.5)' : settings.accentColor }} />
          )}
          <View>
            <Text style={{ fontSize: sizes.name, fontWeight: 'bold', color: hasBg ? '#ffffff' : '#1a202c', lineHeight: 1.2 }}>
              {personalInfo.fullName || 'Your Name'}
            </Text>
            <Text style={{ fontSize: sizes.title, color: hasBg ? 'rgba(255,255,255,0.8)' : '#a0aec0', letterSpacing: 2, marginTop: 2 }}>
              {personalInfo.jobTitle || 'Job Title'}
            </Text>
          </View>
        </View>
        {contactLinks.length > 0 && (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginTop: 8 }}>
            {contactLinks.map((item, i) => (
              <View key={i} style={{ flexDirection: 'row' }}>
                {i > 0 && (
                  <Text style={{ fontSize: sizes.body, color: hasBg ? 'rgba(255,255,255,0.4)' : '#cbd5e0', marginHorizontal: 6 }}>|</Text>
                )}
                <RenderContactItem item={item} style={{ fontSize: sizes.body, color: hasBg ? 'rgba(255,255,255,0.8)' : '#a0aec0' }} />
              </View>
            ))}
          </View>
        )}
      </View>

      {/* Body */}
      <View style={{ paddingHorizontal: 32, paddingTop: 16, paddingBottom: 36 }}>
        {/* Ordered content: About + Sections respecting aboutPosition */}
        {(() => {
          const aboutBlock = personalInfo.summary ? (
            <View key="__about__" style={{ marginBottom: 14 }}>
              <View style={{ borderLeftWidth: 3, borderLeftColor: '#cbd5e0', paddingLeft: 12 }}>
                <Text style={{ fontSize: sizes.body, color: '#4a5568', lineHeight: 1.7, fontStyle: 'italic' }}>
                  {personalInfo.summary}
                </Text>
              </View>
            </View>
          ) : null;
          const pos = data.aboutPosition ?? 0;
          const items: React.ReactNode[] = [];
          sections.forEach((section, i) => {
            if (i === pos && aboutBlock) items.push(aboutBlock);
            items.push(renderSection(section));
          });
          if (pos >= sections.length && aboutBlock) items.push(aboutBlock);
          return items;
        })()}

        {personalInfo.gdprConsent && <RenderGDPR sizes={sizes} accentColor={settings.accentColor} />}
      </View>
    </Page>
  );
};

// ═══════════════════════════════════════════════════════════════════
// MAIN DOCUMENT — template switcher
// ═══════════════════════════════════════════════════════════════════

interface CVPDFDocumentProps {
  data: CVData;
  settings: CVSettings;
  template: CVTemplate;
}

const CVPDFDocument = ({ data, settings, template }: CVPDFDocumentProps) => {
  const renderLayout = () => {
    switch (template) {
      case 'modern':
        return <ModernLayout data={data} settings={settings} />;
      case 'creative':
        return <CreativeLayout data={data} settings={settings} />;
      case 'classic':
      default:
        return <ClassicLayout data={data} settings={settings} />;
    }
  };

  return <Document>{renderLayout()}</Document>;
};

export default CVPDFDocument;
